import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import { usePageMeta } from "@/hooks/usePageMeta";
import { events, sportNames, type SportKey } from "@/mocks/events";

type TimeRange = "all" | "future" | "replay";

const sportFilters: { label: string; value: SportKey | "all" }[] = [
  { label: "全部项目", value: "all" },
  { label: "篮球", value: "basketball" },
  { label: "足球", value: "football" },
  { label: "电竞", value: "esports" },
];

const rangeFilters: { label: string; value: TimeRange }[] = [
  { label: "全部赛程", value: "all" },
  { label: "未来赛事", value: "future" },
  { label: "历史回放", value: "replay" },
];

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const week = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][d.getDay()];
  return { key: `${month}月${day}日`, label: `${month}月${day}日 ${week}` };
}

export default function SchedulePage() {
  const [sport, setSport] = useState<SportKey | "all">("all");
  const [range, setRange] = useState<TimeRange>("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  usePageMeta(
    "赛程与回放 - jrs直播",
    "jrs直播赛程与回放中心，整合篮球、足球、电竞近期赛程与已结束赛事回放入口，支持按项目与日期区间筛选，随时回看精彩对决。",
    "赛程,回放,体育赛程,jrs直播,篮球赛程,足球赛程,电竞赛程",
  );

  const filtered = useMemo(() => {
    return events
      .filter((event) => {
        if (sport !== "all" && event.sport !== sport) return false;
        if (range === "future" && event.status === "ended") return false;
        if (range === "replay" && event.status !== "ended") return false;
        const day = event.date.slice(0, 10);
        if (fromDate && day < fromDate) return false;
        if (toDate && day > toDate) return false;
        return true;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sport, range, fromDate, toDate]);

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: typeof events }>();
    filtered.forEach((event) => {
      const { key, label } = formatDate(event.date);
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key)!.items.push(event);
    });
    return Array.from(map.values());
  }, [filtered]);

  const hasFilter = sport !== "all" || range !== "all" || fromDate || toDate;

  const reset = () => {
    setSport("all");
    setRange("all");
    setFromDate("");
    setToDate("");
  };

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
      <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">赛程与回放</span>
      </nav>

      <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
        赛程与回放
      </h1>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
        按时间轴查看近期赛程安排与已结束赛事的回放入口，支持按项目与日期区间筛选，随时回看那些精彩的瞬间。
      </p>

      <div className="mt-7 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
          <div>
            <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-500">
              <span className="h-1 w-1 rounded-full bg-primary-500" />
              赛事类型
            </div>
            <div className="flex flex-wrap gap-2">
              {sportFilters.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setSport(f.value)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sport === f.value
                      ? "border-primary-500 bg-primary-500/15 text-primary-400"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-500/40 hover:text-foreground-900"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-500">
              <span className="h-1 w-1 rounded-full bg-primary-500" />
              时间范围
            </div>
            <div className="flex flex-wrap gap-2">
              {rangeFilters.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setRange(f.value)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    range === f.value
                      ? "border-primary-500 bg-primary-500/15 text-primary-400"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-500/40 hover:text-foreground-900"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-500">
              <span className="h-1 w-1 rounded-full bg-primary-500" />
              日期区间
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                <i className="ri-calendar-line text-sm text-foreground-500" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-transparent text-sm text-foreground-800 focus:outline-none"
                  aria-label="开始日期"
                />
              </label>
              <span className="text-foreground-500">至</span>
              <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                <i className="ri-calendar-line text-sm text-foreground-500" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-transparent text-sm text-foreground-800 focus:outline-none"
                  aria-label="结束日期"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 场赛事
        </p>
        {hasFilter && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground-500 transition-colors hover:text-primary-400 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      {grouped.length > 0 ? (
        <div className="mt-5 flex flex-col gap-8">
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-8 items-center rounded-md bg-primary-500/15 px-3 font-heading text-sm font-bold text-primary-400">
                  {group.label}
                </span>
                <span className="h-px flex-1 bg-background-200" />
                <span className="text-xs text-foreground-500">
                  {group.items.length} 场
                </span>
              </div>
              <div className="flex flex-col gap-3">
                {group.items.map((event) => (
                  <div
                    key={event.id}
                    className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-500/50 lg:flex-row lg:items-center lg:gap-6 lg:p-5"
                  >
                    <div className="flex items-center gap-4 lg:w-[320px]">
                      <div className="flex flex-col items-center gap-1">
                        <TeamBadge team={event.home} size={34} />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-heading text-sm font-semibold text-foreground-950">
                          {event.title}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-500">
                          <span>{event.league}</span>
                          <span>·</span>
                          <span>{event.time}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <StatusBadge status={event.status} />
                      {event.status !== "upcoming" ? (
                        <span className="font-heading text-lg font-bold text-foreground-900">
                          {event.homeScore} : {event.awayScore}
                        </span>
                      ) : (
                        <span className="font-heading text-sm font-bold text-foreground-500">
                          VS
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 lg:ml-auto">
                      <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-map-pin-2-line" />
                        {event.venue}
                      </span>
                      <Link
                        to={`/live/${event.id}`}
                        className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                      >
                        <i
                          className={
                            event.status === "ended"
                              ? "ri-play-circle-line"
                              : "ri-live-line"
                          }
                        />
                        {event.status === "ended"
                          ? "观看回放"
                          : event.status === "live"
                            ? "看直播"
                            : "查看详情"}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-calendar-close-line text-3xl" />
          </span>
          <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
            该时间范围内暂无赛事
          </h2>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            当前筛选条件下没有找到赛事，试试放宽日期区间或切换赛事类型。
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选条件
          </button>
        </div>
      )}

      <p className="mt-8 flex items-center gap-2 text-xs text-foreground-500">
        <i className="ri-information-line text-primary-400" />
        回放播放功能为演示展示，未接入真实流媒体信号。
      </p>
      <span className="sr-only">
        {sport !== "all" ? sportNames[sport] : "全部项目"}赛程
      </span>
    </section>
  );
}